import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { NavLink, useLocation } from 'react-router-dom';
import cn from 'classnames';

import useClickOutside from 'utils/useClickOutside';
import useResponsiveQuery from 'utils/useResponsiveQuery';
import ChevronIcon from 'components/common/icons/ChevronIcon';

import css from './styles.module.scss';
import cssHeader from '../styles.module.scss';

export type DropdownId = 'product' | 'analytics' | 'solutions' | undefined;

interface MenuItemType {
  label: string;
  subtitle?: string;
  url: string;
}

type Props = {
  title: string;
  titleClassName?: string;
  dropdownLinks: MenuItemType[];
  invertColors?: boolean;
  id?: DropdownId;
  activeDropdownId?: string;
  onClick?: React.Dispatch<React.SetStateAction<DropdownId>>;
  colls?: number[]; // each element show how many items in column
};

function sum(arr: number[]) {
  return arr.reduce(function (sum, elem) {
    return sum + elem;
  }, 0);
}

function DropdownMenu({ title, titleClassName, dropdownLinks, invertColors, colls, ...props }: Props) {
  const isMobile = useResponsiveQuery();
  const isOpen = props.activeDropdownId !== undefined && props.id === props.activeDropdownId;
  const toggleMenu = () => props.onClick?.((id) => (id === props.id ? undefined : props.id));
  const { search } = useLocation();

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const menuNode = menuRef.current;
    const dropdownNode = dropdownRef.current;
    const initialHeight = menuNode?.clientHeight ?? 0;
    const dropdownHeight = dropdownNode?.clientHeight ?? 0;

    if (menuNode && isOpen && isMobile) {
      gsap.to(menuNode, { height: initialHeight + dropdownHeight, duration: 0.15 });
    }

    return () => {
      if (menuNode) {
        gsap.to(menuNode, { height: initialHeight, duration: 0.15 });
      }
    };
  }, [isOpen, isMobile]);

  useClickOutside(menuRef, () => {
    if (isOpen && !isMobile) toggleMenu();
  });

  const renderColls = () => {
    if (colls) {
      return colls.map((col, index) => {
        const items =
          index === 0
            ? dropdownLinks.slice(0, colls[index])
            : dropdownLinks.slice(sum(colls.slice(0, index)), sum(colls.slice(0, index)) + colls[index]);

        return (
          <div className={css.column}>
            {items.map((item) => (
              <NavLink key={item.url} to={`${item.url}${search}`} activeClassName={css.navLinkActive}>
                <span className={css.title}>{item.label}</span>
                <span className={css.subtitle}>{item.subtitle}</span>
              </NavLink>
            ))}
          </div>
        );
      });
    }
  };

  if (isMobile) {
    return (
      <div
        className={cn(css.dropdownMenu, 'relative overflow-hidden', { [css.isOpen]: isOpen })}
        onClick={toggleMenu}
        ref={menuRef}
      >
        <div className={titleClassName}>
          {title}
          <ChevronIcon className={css.dropdownIcon} />
        </div>
        <div ref={dropdownRef} className={cn(css.dropdown, 'flex', 'flex-col', 'absolute')}>
          {dropdownLinks.map((item) => (
            <NavLink key={item.url} to={`${item.url}${search}`} activeClassName={cssHeader.navLinkActive}>
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>
    );
  }

  // *** Desktop ***
  return (
    <div className={cn(css.menuDesktop, 'flex items-center relative')} onClick={toggleMenu} ref={menuRef}>
      <div className={titleClassName}>
        {title}
        <ChevronIcon className={css.dropdownIcon} />
      </div>
      {colls ? (
        <div
          className={cn(
            css.dropdownBig,
            'flex flex-row absolute duration-100',
            invertColors ? 'bg-black text-white' : 'bg-white text-black',
            isOpen ? 'visible opacity-100' : 'invisible opacity-0',
          )}
        >
          {renderColls()}
        </div>
      ) : (
        <div
          className={cn(
            css.dropdown,
            'flex flex-col absolute duration-100',
            invertColors ? 'bg-black text-white' : 'bg-white text-black',
            isOpen ? 'visible opacity-100' : 'invisible opacity-0',
          )}
        >
          {dropdownLinks.map((item) => (
            <NavLink key={item.url} to={`${item.url}${search}`} activeClassName={css.navLinkActive}>
              <span className={css.title}>{item.label}</span>
              <span className={css.subtitle}>{item.subtitle}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default DropdownMenu;
